import type { $TSFixMe } from '@readme/backend/iso';
import type { ReactNode } from 'react';

import { useContext, useEffect } from 'react';

import { ProjectContext } from '@core/context';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { getIframePreviewFlags } from '@core/utils/iframe';

import liveUpdateHub2CSS from './liveUpdateHub2CSS';

/**
 * Use this component for logic that needs to modify the document outside of the Hub's react root element
 * based on changes to the project store.
 *
 * Most updates should be handled by Hub components reactively updating from the project
 * store state, but there are some cases where we need to make updates to the Document DOM
 * outside of the Hub's root element.  For example updating the custom CSS or swapping a favicon.
 */
export function ConnectProjectStoreToDocument({ children }: { children?: ReactNode }) {
  // @todo: Using ProjectContext is a temporary solution until we can get the parent data in the apiV2 project representation
  const { project } = useContext(ProjectContext) as { project: $TSFixMe };
  const parentCSS = project?.parent?.appearance?.stylesheet_hub2 as string | undefined;
  const disableSuperframe = !!project?.flags?.disableSuperframe;

  const projectCSS = useProjectStore(s => s.data?.appearance?.custom_code?.css);
  const hasStylesheetPermission = useProjectStore(s => s.plan.features.stylesheet);
  const [isEditing, isNewIframeStructure, isSettingsLayout, isDemoing] = useSuperHubStore(s => [
    s.isEditing,
    s.isNewIframeStructure,
    s.layout === 'settings',
    s.isDemoing,
  ]);

  useEffect(() => {
    const { isIframePreview } = getIframePreviewFlags();
    // When custom CSS is sandboxed into an iframe preview, do not apply it to the parent admin document.
    const isInParentAdminDocument = !isNewIframeStructure && !isIframePreview && !disableSuperframe;

    // See if we should disable custom CSS via queryparams.
    const searchParamsLowerCased = new URLSearchParams(window.location.search.toLowerCase());
    const disableCustomCss = searchParamsLowerCased.get('disablecustomcss') === 'true';

    // Only apply custom CSS styles when viewing pages. We clear custom CSS from the document when editing
    // or when viewing a settings layout where the Hub is not previewed.
    const styles =
      isInParentAdminDocument || isEditing || isSettingsLayout || disableCustomCss
        ? ''
        : [parentCSS, hasStylesheetPermission || isDemoing ? projectCSS : null].filter(Boolean).join('');
    liveUpdateHub2CSS(styles);
  }, [
    disableSuperframe,
    hasStylesheetPermission,
    isDemoing,
    isEditing,
    isNewIframeStructure,
    isSettingsLayout,
    parentCSS,
    projectCSS,
  ]);

  return <>{children}</>;
}
